import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import {
  useParams,
  useNavigate,
  Link
} from "react-router-dom";
import { CallAuthGet, CallAuthPost } from '../Services/lib'; // Assuming API utilities are exported from here
import Button from 'react-bootstrap/Button';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import InnerLayer from '../components/InnerLayer';
import SingleGround from '../components/SingleGround'; // Reusing SingleGround component if needed
import axios from 'axios';
import { BASE_URL, Slots, OneHourSlots } from '../uti/uti';

const SwapBooking = observer(() => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [bookingDetails, setBookingDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(false);

  // Constants
  const CONFIRM_SWAP_API_URL = '/ground-booking/swap-slot'; // PLACEHOLDER: Update this when API is confirmed

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  useEffect(() => {
    if (bookingDetails?.ground?._id) {
      fetchSlots();
    }
  }, [selectedDate, bookingDetails]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const res = await CallAuthGet(`/ground-bookings/${bookingId}`);
      if (res && res.result) {
        setBookingDetails(res.result);
        if (res.result.startDate) {
            setSelectedDate(new Date(res.result.startDate));
        }
      } else {
        toast.error("Failed to load booking details");
      }
    } catch (error) {
      console.error("Error fetching booking details", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async () => {
    if (!bookingDetails?.ground?._id) return;

    try {
      setLoading(true);
      setSlots([]); // Clear previous slots
        
      // Using the API found in ManageGround.js
      const startOfDay = moment(selectedDate).startOf('day').toISOString();
      const endOfDay = moment(selectedDate).endOf('day').toISOString();

        const res = await axios.get(BASE_URL + `/grounds/book-history/${bookingDetails.ground._id}?startDate=${startOfDay}&endDate=${endOfDay}`)

      if (res?.data?.result) {
        
        // Determine which slot set to use
        const baseSlots = bookingDetails?.ground?.slotDurationMinutes == 60 ? OneHourSlots : Slots;
        const bookedSlots = res.data.result; // Array of bookings

        const mergedSlots = baseSlots.map(slot => {
            const [startH, startM] = slot.start.split(':');
            const [endH, endM] = slot.end.split(':');
            
            const slotStart = moment(selectedDate).set({hour: parseInt(startH), minute: parseInt(startM), second: 0, millisecond:0});
            const slotEnd = moment(selectedDate).set({hour: parseInt(endH), minute: parseInt(endM), second: 0, millisecond:0});

            const isBooked = bookedSlots.some(booking => {
                // Check for overlap
                const bookingStart = moment(booking.startDate);
                const bookingEnd = moment(booking.endDate);
                
                return slotStart.isBefore(bookingEnd) && slotEnd.isAfter(bookingStart); 
            });
            
             // Check if this is the CURRENT booking we are swapping
            const isCurrentBooking = bookingDetails._id && bookedSlots.some(b => b._id === bookingDetails._id && 
                 moment(b.startDate).isSame(slotStart) // specific check
            );


            return {
                ...slot,
                start: slotStart.toISOString(),
                end: slotEnd.toISOString(),
                status: isBooked ? 'Booked' : 'Available',
                isCurrent: isCurrentBooking
            };
        });

        setSlots(mergedSlots);

      }
    } catch (error) {
      console.error("Error fetching slots", error);
      toast.error("Failed to fetch slots");
    } finally {
      setLoading(false);
    }
  };

  console.log("selectedSlot", selectedSlot)
  

  const handleConfirmSwap = async () => {
    if (!selectedSlot) return;
    
    // Safety check
    if (window.confirm("Are you sure you want to swap the booking to this new slot?")) {
        try {
            setSwapping(true);
            const res = await CallAuthPost(CONFIRM_SWAP_API_URL, {
                bookingId: bookingId,
                startDate: selectedSlot.start,
                endDate: selectedSlot.end,
            });

            if (res && res.success) {
                toast.success("Slot swapped successfully!");
                navigate('/bookings');
            } else {
                toast.error(res?.message || "Failed to swap slot");
            }

        } catch (error) {
            console.error("Error swapping slot", error);
            toast.error("An error occurred while swapping.");
        } finally {
            setSwapping(false);
        }
    }
  };

  return (
    <InnerLayer>
        <div className='screenContainer'>
            <Stack direction="horizontal" gap={3} className="mb-4">
                <Button variant="outline-secondary" onClick={() => navigate('/bookings')}>
                     &larr; Back
                </Button>
                <h4 className='mb-0'>Swap Slot for Booking #{bookingDetails?.orderId || bookingDetails?._id?.slice(-6)}</h4>
            </Stack>

            <div className="row">
                <div className="col-md-4">
                    <div className="card shadow-sm p-3 mb-3">
                         <h5 className="card-title">Select Date</h5>
                        <DatePicker
                            selected={selectedDate}
                            onChange={(date) => setSelectedDate(date)}
                            inline
                        />
                    </div>
                     {bookingDetails && (
                        <div className="card shadow-sm p-3">
                            <h5 className="card-title">Current Booking</h5>
                            <p><strong>Ground:</strong> {bookingDetails.ground?.title}</p>
                            <p><strong>Date:</strong> {moment(bookingDetails.startDate).format('Do MMM YYYY')}</p>
                            <p><strong>Time:</strong> {moment(bookingDetails.startDate).format('hh:mm A')} - {moment(bookingDetails.endDate).format('hh:mm A')}</p>
                        </div>
                     )}
                </div>

                <div className="col-md-8">
                    <div className="card shadow-sm p-3">
                        <Stack direction="horizontal" gap={2} className="mb-3">
                             <h5 className="mb-0">Available Slots</h5>
                             {/* Legend */}
                             <div className="ms-auto d-flex gap-2">
                                <span className="badge bg-success">Available</span>
                                <span className="badge bg-secondary">Booked</span>
                                <span className="badge bg-primary">Selected</span>
                             </div>
                        </Stack>

                        <div className="slot-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                            {slots.map((slot, index) => (
                                <button
                                    key={index}
                                    className={`btn ${
                                        selectedSlot === slot ? 'btn-primary' : 
                                        slot.status === 'Booked' ? 'btn-secondary disabled' : 'btn-outline-success'
                                    }`}
                                    disabled={slot.status === 'Booked'}
                                    onClick={() => setSelectedSlot(slot)}
                                    style={{ padding: '10px' }}
                                >
                                    {slot.label}
                                    <br/>
                                    <small>{slot.status}</small>
                                </button>
                            ))}
                            {slots.length === 0 && !loading && <p className="text-muted">No slots generated or found.</p>}
                        </div>
                    </div>
                    
                    <div className="mt-4 d-flex justify-content-end">
                        <Button 
                            variant="primary" 
                            size="lg" 
                            disabled={!selectedSlot || swapping}
                            onClick={handleConfirmSwap}
                        >
                            {swapping ? 'Swapping...' : 'Confirm Swap'}
                        </Button>
                    </div>

                </div>
            </div>
        </div>
    </InnerLayer>
  );
});

export default SwapBooking;
